import { useState, useRef, useEffect } from "react";
import Browser from "../browser";
import Device from "../models/Device";
import { ChannelType } from "../models/ChannelType";
import { useOnChangeWatcher } from "../models/useOnChangeWatcher";
import { showGlobalWaitIndicator } from "./GlobalWaitIndicator";
import { useRedraw } from "../models/useRerender";
import { uiModeWatcher } from "../uiMode";
import { sleep } from "../models/utils";
import KnxDetails from "./KnxParameterViewer/KnxDetails";
import { getKnxParameterViewerForWindow } from "./KnxParameterViewer/KnxParameterViewerCache";

export default function ParameterViewerWrapper(props: { key?: string; device: Device }) {
  const { device } = props;
  const [isLandscape, setIsLandscape] = useState(Browser.isLandScape());
  const [leftPanelWidth, setLeftPanelWidth] = useState(300);
  const alignToMaxWidthTimer = useRef<any>(null);
  const dragInfoRef = useRef<{ startX: number; startWidth: number } | null>(null);

  useOnChangeWatcher(uiModeWatcher);

  const redraw = useRedraw();

  const timer = useRef<any>(null);

  function updateParameterSeparatorHeight(): boolean {
    let reRun = false;

    const parameterSeparators = knxParameterViewerRef.current?.querySelectorAll(".ParameterSeparator");

    parameterSeparators?.forEach((parameterSeparator) => {
      const child = parameterSeparator.firstChild;
      if (child) {
        let h = (child as HTMLDivElement).getBoundingClientRect().height;

        // magic fix for some magic error
        if (h < 10) {
          h = 50;
          reRun = true;
        }

        (parameterSeparator as HTMLDivElement).style.height = `${h}px`;
      }
    });

    return reRun;
  }

  const knxParameterViewerRef = useRef<HTMLDivElement>(null);

  useOnChangeWatcher(device.template?.knxProductData, (_changedProperty, _changedObject) => {
    init();
    return false;
  });

  let knxInstanceData = device.knxInstanceData;

  useOnChangeWatcher(device.knxParameterViewer, (changedProperty, changedObject) => {
    if (changedProperty == "redraw") {
      return true; // redraw
    }

    return false;
  });

  useOnChangeWatcher(device.knxInstanceData, (changedProperty, changedObject) => {
    if (device.reEvalKnxMetadataNeeded) {
      device.updateAllFromKnxMetadataImmediately().then(() => {
        redraw();
      });
    }
    return false;
  });

  useOnChangeWatcher(uiModeWatcher, () => {
    device.updateAllFromKnxMetadataImmediately().then(() => {
      redraw();
    });
    return false;
  });

  async function init() {
    if (!device.knxParameterViewer.value || device.reEvalKnxMetadataNeeded) {
      showGlobalWaitIndicator(
        true,
        state.translate("deviceTemplatePropertieView.loadProductData", "lade Produktdaten...."),
      );

      await sleep(10);

      device.updateAllFromKnxMetadataImmediately();

      // await device.template?.knxProductData.loadXmlData();
      // await device.template?.knxProductData.createRefMap();
      // await device.template?.knxProductData.loadIconFile();

      // const v = await KnxParameterViewer({
      //   device: device,
      //   mode: DisplayMode.Parameters,
      //   isActor: device.isActor,
      //   xchannels: new Array<Channel>(),
      //   isLandscape: isLandscape,
      // });
      // device.reEvalKnxMetadataNeeded = false;

      // setKnxParameterViewer(v);
    }
  }

  useEffect(() => {
    function onOrientationChange(_ev: any) {
      setTimeout(() => {
        //  setIsLandscape(Browser.isLandScape());
      }, 100);
    }
    window.addEventListener("orientationchange", onOrientationChange);

    init();

    return () => {
      window.removeEventListener("orientationchange", onOrientationChange);
    };
  }, []);

  useEffect(() => {
    if (alignToMaxWidthTimer.current) {
      clearTimeout(alignToMaxWidthTimer.current);
    }
    alignToMaxWidthTimer.current = setTimeout(() => {
      alignToMaxWidthTimer.current = 0;
      const elms = knxParameterViewerRef.current?.querySelectorAll(".alignToMaxWidth");
      const w = knxParameterViewerRef.current?.scrollWidth;
      if (elms && w) {
        for (let i = 0; i < elms.length; i++) {
          const elm = elms[i] as HTMLElement;
          elm.style.width = `${w}px`;
          elm.style.maxWidth = "inherit";
        }
      }
      showGlobalWaitIndicator(false);
    }, 100);

    return () => {
      if (alignToMaxWidthTimer.current) {
        clearTimeout(alignToMaxWidthTimer.current);
        alignToMaxWidthTimer.current = null;
      }
    };
  });

  useEffect(() => {
    function onMouseMove(ev: MouseEvent) {
      const dragInfo = dragInfoRef.current;
      const container = knxParameterViewerRef.current;
      if (!dragInfo || !container) {
        return;
      }

      const containerWidth = container.getBoundingClientRect().width;
      const minDetailsWidth = 180;
      const minLeftWidth = 150;
      const maxLeftWidth = Math.max(minLeftWidth, containerWidth - minDetailsWidth);

      const dx = ev.clientX - dragInfo.startX;
      const nextWidth = dragInfo.startWidth + dx;
      const clampedWidth = Math.min(maxLeftWidth, Math.max(minLeftWidth, nextWidth));

      setLeftPanelWidth(clampedWidth);
    }

    function onMouseUp() {
      dragInfoRef.current = null;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    }

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);

    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, []);

  function onSplitterMouseDown(ev: React.MouseEvent<HTMLDivElement>) {
    dragInfoRef.current = {
      startX: ev.clientX,
      startWidth: leftPanelWidth,
    };
    document.body.style.cursor = "col-resize"; // no itx
    document.body.style.userSelect = "none";
  }

  if (!device.template) {
    if (device.channelType === ChannelType.Lan) return null;
    return <div>{state.translate("deviceTemplatePropertieView.noProductSelected", "No Product selected ")}</div>;
  }

  if (device.knxParameterViewer.value) {
    const viewer = getKnxParameterViewerForWindow(device.knxParameterViewer.value, device, window);
    return (
      <div id={`ParameterViewer-${isLandscape}`} ref={knxParameterViewerRef} className="estadaRow left top w100 h100">
        <div
          style={{
            width: `${leftPanelWidth}px`,
            minWidth: `${leftPanelWidth}px`,
            flexShrink: "0",
            height: "100%",
            overflow: "auto",
          }}
        >
          {viewer}
        </div>
        <div
          onMouseDown={onSplitterMouseDown}
          style={{
            width: "8px",
            minWidth: "8px",
            height: "100%",
            cursor: "col-resize",
            backgroundColor: "var(--panel-background)",
          }}
        ></div>
        <KnxDetails device={device} />
      </div>
    );
  } else {
    //
    // knxProductData not yet loaded
    //
    showGlobalWaitIndicator(
      true,
      state.translate("deviceTemplatePropertieView.loadProductData", "lade Produktdaten...."),
    );

    return <div></div>;
  }
}
